import { iconButtonClasses } from '@mui/material/IconButton'
import { paperClasses } from '@mui/material/Paper'
import { type CSSProperties, createTheme } from '@mui/material/styles'
import { typographyClasses } from '@mui/material/Typography'

export type RoundedKey = 'sm' | 'md' | 'lg' | 'full'

export function normalizeRounded(
	value: unknown,
): boolean | RoundedKey | undefined {
	if (typeof value === 'boolean') return value
	if (value === 'sm' || value === 'md' || value === 'lg' || value === 'full') {
		return value
	}
	return undefined
}

export function resolveRounded(
	theme: ReturnType<typeof createTheme>,
	rounded: boolean | RoundedKey | undefined,
	additionalStyles?: ({
		borderRadius,
	}: CSSProperties &
		Required<Pick<CSSProperties, 'borderRadius'>>) => CSSProperties,
): CSSProperties | undefined {
	if (!rounded) return undefined
	let borderRadius: number
	if (rounded === true) {
		borderRadius = theme.shape.radii.full
	} else {
		borderRadius = theme.shape.radii[rounded]
	}
	if (additionalStyles) {
		return additionalStyles({ borderRadius })
	}
	return { borderRadius }
}

// Use CSS variables here once the design tokens migration is complete.

export const theme = createTheme({
	cssVariables: true,
	palette: {
		primary: {
			main: '#000045',
			light: '#5B4B8A',
			dark: '#2B1A6D',
		},
		secondary: {
			main: '#808AFF',
			light: '#A6C8FF',
			dark: '#0056B3',
		},
		text: {
			primary: '#040D26',
			secondary: '#555',
		},
	},
	shape: {
		borderRadius: 6,
		radii: { sm: 4, md: 8, lg: 16, full: 999 },
	},
	typography: {
		fontFamily: 'Calibri, system',
		h2: {
			fontSize: '1.5rem',
			fontWeight: '400',
			color: '#000045',
		},
	},
	components: {
		MuiAlert: {
			styleOverrides: {
				root: {
					paddingBlock: '2px',
					// maxHeight: '40px',
				},
			},
		},
		MuiButton: {
			defaultProps: {
				variant: 'contained',
				color: 'primary',
				size: 'large',
			},
			styleOverrides: {
				root: ({ ownerState, theme }) => {
					const rounded = normalizeRounded(ownerState.rounded)
					return {
						textTransform: 'none',
						fontWeight: '400',
						...resolveRounded(theme, rounded, (cssProps) => ({
							...cssProps,
							padding: '0 24px',
						})),
					}
				},
				containedSecondary: {
					backgroundColor: '#E5004C',
					color: '#fff',
					'&:hover': {
						backgroundColor: '#C2003C',
					},
				},
			},

			variants: [
				{
					props: { size: 'small' },
					style: {
						height: 35,
					},
				},
				{
					props: { size: 'large' },
					style: {
						height: 40,
					},
				},
				{
					props: { variant: 'text' },
					style: {
						color: 'inherit',
						'&:hover': {
							backgroundColor: 'rgba(0,0,0, 0.2)',
						},
					},
				},
				{
					props: { color: 'inherit', variant: 'text' },
					style: {
						'&:hover': {
							backgroundColor: 'rgba(255,255,255, 0.2)',
						},
					},
				},
			],
		},
		MuiAutocomplete: {
			defaultProps: {
				size: 'small',
			},
		},
		MuiBreadcrumbs: {
			styleOverrides: {
				root: {
					minBlockSize: 40,
					display: 'flex',
					alignItems: 'center',
					marginInlineStart: 0,
					'& li:last-child *': {
						fontWeight: '700',
					},
				},
				separator: {
					marginInline: '3px',
				},
			},
		},
		MuiCircularProgress: {
			defaultProps: {
				size: 30,
			},
		},
		MuiDialog: {
			defaultProps: {
				maxWidth: 'xs',
				fullWidth: true,
			},
			variants: [
				{
					props: {
						maxWidth: 'xs',
					},
					style: {
						[`.${paperClasses.root}`]: {
							maxWidth: 368,
						},
					},
				},
				{
					props: {
						maxWidth: 'sm',
					},
					style: {
						[`.${paperClasses.root}`]: {
							maxWidth: 515,
						},
					},
				},
			],
		},
		MuiFormGroup: {
			styleOverrides: {
				root: {
					display: 'flex',
					flexDirection: 'column',
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: () => ({
					marginLeft: '0',

					[`.${typographyClasses.root}`]: {
						fontSize: '16px',
					},
				}),
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				outlined: {
					color: '#6B7280',
					fontSize: '0.95rem',
					'&.MuiInputLabel-shrink': {
						backgroundColor: '#fff',
						paddingInline: '6px',
						marginLeft: '8px',
						borderRadius: '4px',
					},
				},
			},
		},
		MuiOutlinedInput: {
			defaultProps: {
				notched: false,
				className: 'pill',
				inputProps: {
					notched: false,
				},
			},
			styleOverrides: {
				root: {
					backgroundColor: '#fff',
					borderRadius: '8px',
					width: '100%',
					fontSize: '1rem',
					padding: '0',
					transition: 'box-shadow 0.2s, border-color 0.2s',
					boxShadow: '0 0 0 0px #000045',
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: '#000045',
					},
					'&.Mui-error .MuiOutlinedInput-notchedOutline': {
						borderColor: '#E5004C',
						borderWidth: '1px', // Ensure it doesn't disappear
					},

					'&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
						borderColor: '#B0B0B0',
						boxShadow: 'none',
					},
					'&.pill': {
						borderRadius: '999px',
					},
					'&.MuiInputLabel-shrink': {
						backgroundColor: 'white',
						padding: '0 24px',
						marginLeft: '18px',
					},
					'&:hover .Mui-error .MuiOutlinedInput-notchedOutline': {
						borderColor: '#E5004C',
					},
					'&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
						borderColor: '#E5004C',
					},
				},
				notchedOutline: {
					borderColor: '#B0B0B0',
					transition: 'border-color 0.2s',
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
				size: 'small',
				fullWidth: true,
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: '999px',
				},
				select: {
					display: 'flex',
					alignItems: 'center',
					minHeight: '1.4375em',
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					display: 'inline-flex',
					width: 'fit-content',
					maxWidth: '100%',
					backgroundColor: '#E7EAF0',
					borderRadius: 34,
					padding: '4px 8px',
					minHeight: '48px',
					overflow: 'hidden',
				},
				flexContainer: {
					gap: '6px',
				},
				indicator: {
					height: 'calc(100% - 8px)',
					top: '4px',
					bottom: 'auto',
					borderRadius: 34,
					backgroundColor: '#000045',
					boxShadow: '0 1px 6px rgba(0, 0, 69, 0.2)',
					transition:
						'left 120ms ease, width 120ms ease, background-color 120ms ease',
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: 'capitalize',
					fontWeight: 700,
					letterSpacing: '-0.01em',
					minHeight: '40px',
					minWidth: 0,
					padding: '6px 22px',
					borderRadius: 34,
					color: '#4A5568',
					zIndex: 1,
					transition: 'background-color 120ms ease, color 120ms ease',
					'&:hover': {
						color: '#000045',
					},
					'&.Mui-selected': {
						color: '#fff',
					},
				},
			},
		},
		MuiTypography: {
			styleOverrides: {},
			variants: [
				{
					props: { variant: 'body1' },
					style: {
						'&[data-grid-item="__full"]:first-child': {
							paddingBlockStart: '0',
						},
					},
				},
				{
					props: { variant: 'subtitle1' },
					style: {
						fontSize: '18px',
						fontWeight: '700',
						lineHeight: '1.25',
					},
				},
				{
					props: { variant: 'caption' },
					style: {
						fontSize: '12px',
						fontWeight: '400',
						lineHeight: '1',
					},
				},
			],
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					paddingBlock: '4px',
				},
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					color: 'currentColor',
					minWidth: 'auto',
					[`.${iconButtonClasses.root}`]: {
						marginInlineStart: '0px',
					},
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					color: 'currentColor',
					padding: '10px',
					':hover': {
						backgroundColor: 'rgba(255,255,255, 0.2)',
					},
					'&.Mui-disabled': {
						color: 'color-mix(in oklab, currentColor 80%, transparent 100% )',
					},
				},
			},
		},
		MuiSvgIcon: {
			variants: [
				{
					props: {
						fontSize: 'medium',
					},
					style: {
						fontSize: '20px',
					},
				},
			],
		},
		MuiTableCell: {
			styleOverrides: {
				head: {
					fontWeight: 'bold',
				},
			},
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					fontSize: '1.5rem',
					fontWeight: '700',
					color: 'primary.main',
				},
			},
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					color: 'primary.main',
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					marginLeft: '16px',
				},
			},
		},
	},
})
